
import { useState, useEffect, useContext } from 'react';
import { Tooltip, IconButton, Typography, Button, Grid, CircularProgress, FormControlLabel, Checkbox, Divider } from '@material-ui/core';
import { Dropzone } from 'components';
import { Field, useFormikContext, FieldArray, FieldProps, ErrorMessage } from 'formik';
import axios from 'axios';
import { AttachFile as AttachmentIcon, Close as CancelIcon, KeyboardArrowDown as MdKeyboardArrowDown, KeyboardArrowUp as MdKeyboardArrowUp } from '@material-ui/icons';
import { IMessage, messageContext, messageSchema } from './MessagesContext';
import * as yup from 'yup';
import ReactQuill from 'react-quill';
import { SaveResponse } from 'helpers/interfaces';
import { appUserContext } from 'helpers/services';
import { UserType } from 'helpers/enums';


interface IProps {
    ticketId: string
    editMessage?: IMessage
    onClose?: () => void
}

let MessageEditor = (props: IProps) => {
    let { currentUser } = useContext(appUserContext);
    let { isSaving } = useContext(messageContext);

    let [showDropzone, setShowDropzone] = useState<boolean>(false);
    let [isUploading, setUploading] = useState<boolean>(false);

    type SaveMessage = yup.InferType<typeof messageSchema>;

    let form = useFormikContext<SaveMessage>();


    useEffect(() => {
        form.setFieldValue('ticketId', props.ticketId);

        if (props.editMessage) {
            form.setFieldValue('messageId', props.editMessage.messageId);
            form.setFieldValue('text', props.editMessage.text);
            form.setFieldValue('attachments', props.editMessage.attachments);
        }
    }, [props.editMessage])


    let dropCallback = (files: any[]) => {
        files.forEach(async (element: any) => {
            try {
                setUploading(true);
                let formData = new FormData();
                formData.append("file", element);

                let res = await axios.post<SaveResponse>('/api/TicketMessage/UploadAttachment', formData);

                form.values.attachments.push({
                    fileId: res.data.id,
                    fileName: element.name
                });

                form.setFieldValue('attachments', [...form.values.attachments]);

            } catch (err) {
                form.setFieldError("attachments", "Error during file upload");
            } finally {
                setUploading(false);
            }
        })
    }

    return (
        <>
            <Grid container>
                <Grid item xs={12} style={{ height: 216 }}>
                    <ErrorMessage name="text">
                        {msg => <Typography variant="caption" color="error">{msg}</Typography>}
                    </ErrorMessage>
                    <Field name="text">
                        {({ field }: FieldProps) => <ReactQuill style={{ height: 144 }} value={field.value} onChange={field.onChange(field.name)} />}
                    </Field>
                </Grid>
                <Grid item xs>
                    <Button variant="outlined" onClick={() => setShowDropzone(!showDropzone)}>
                        {showDropzone ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}&nbsp;<AttachmentIcon />
                    </Button>
                </Grid>
                {props.onClose !== undefined &&
                    <Grid item>
                        <Button
                            variant="outlined"
                            disabled={isUploading}
                            onClick={props.onClose}
                        >
                            Cancel
                        </Button>
                    </Grid>
                }
                {currentUser?.typology === UserType.VitecUser &&
                    <>
                        <Grid item>
                            <Button
                                color="secondary"
                                variant="outlined"
                                disabled={isUploading || isSaving}
                                onClick={() => {
                                    form.setFieldValue('isPublic', false);
                                    form.submitForm();
                                }}
                            >
                                Add internal note
                            </Button>
                        </Grid>
                        {currentUser?.canReplyToCustomer === true &&
                            <Grid item>
                                <Divider orientation="vertical" />
                            </Grid>
                        }
                    </>
                }
                {(currentUser?.typology === UserType.Customer || currentUser?.canReplyToCustomer === true) &&
                    <>
                        <Grid item>
                            <FormControlLabel
                                label="I’m waiting for a reply"
                                name="waitingForReply"
                                value={form.values.waitingForReply}
                                checked={form.values.waitingForReply}
                                onChange={(e: any) => form.setFieldValue('waitingForReply', e.target.checked)}
                                control={<Field type="checkbox" name="waitingForReply" component={Checkbox} />}
                            />
                        </Grid>
                        <Grid item>
                            <Button
                                color="secondary"
                                variant="contained"
                                disabled={isUploading || isSaving}
                                onClick={() => {
                                    form.setFieldValue('isPublic', true);
                                    form.submitForm();
                                }}
                            >
                                {currentUser?.typology === UserType.Customer && 'Send message'}
                                {currentUser?.typology === UserType.VitecUser && 'Reply to reporter'}
                            </Button>
                        </Grid>
                    </>
                }
            </Grid>

            {showDropzone && <Dropzone isMultiple={true} onDropCallback={dropCallback} />}
            {isUploading && <CircularProgress size={20} />}

            {form.values.attachments.length > 0 &&
                <FieldArray name="attachments">
                    {arrayHelper =>
                        form.values.attachments.map((file, index) =>
                            <Typography key={index}>
                                File uploaded:&nbsp;{file.fileName}
                                <Tooltip arrow title="Remove">
                                    <IconButton
                                        size="small"
                                        onClick={() => arrayHelper.remove(index)}
                                    >
                                        <CancelIcon />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                        )
                    }
                </FieldArray>
            }
        </>
    )
}

export default MessageEditor;